import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail, auth, db, functions } from '../firebase';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { Helmet } from 'react-helmet';
import MarketingLayout from '../marketing/layout/MarketingLayout';
import { readFirstTouchForSignup } from '../services/acquisitionService';
import type { FirstTouchPayload } from '../types/acquisition';

interface AuthFormProps {
  mode?: 'signup' | 'login';
  prefilledEmail?: string;
}

type Notice = { kind: 'error' | 'info'; message: string } | null;

export default function AuthForm({ mode = 'signup', prefilledEmail = '' }: AuthFormProps) {
  const [email, setEmail] = useState<string>(prefilledEmail);
  const [pw, setPw] = useState<string>('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [postcode, setPostcode] = useState('');
  const [notice, setNotice] = useState<Notice>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const nav = useNavigate();

  useEffect(() => {
    if (prefilledEmail) setEmail(prefilledEmail);
  }, [prefilledEmail]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNotice(null);
    setIsLoading(true);

    try {
      if (mode === 'signup') {
        const trimmedFirst = firstName.trim();
        const trimmedLast = lastName.trim();
        if (!trimmedFirst || !trimmedLast) {
          setNotice({ kind: 'error', message: 'Please enter your first and last name.' });
          setIsLoading(false);
          return;
        }
        const userCredential = await createUserWithEmailAndPassword(auth, email, pw);
        const user = userCredential.user;
        const displayName = `${trimmedFirst} ${trimmedLast}`.trim();
        await setDoc(doc(db, 'users', user.uid), {
          uid: user.uid,
          email: user.email,
          firstName: trimmedFirst,
          lastName: trimmedLast,
          displayName,
          phone: phone.trim(),
          shippingAddress: {
            line1: '',
            line2: '',
            city: '',
            postcode: postcode.trim(),
            country: 'United Kingdom',
          },
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        // Acquisition first-touch: read the cookie set by
        // CampaignLandingPage / ReferralLandingPage / HomePage and write
        // the immutable acquisition block via the server. Failure here is
        // non-fatal — the user is signed up regardless. See
        // src/services/acquisitionService.ts and
        // docs/markdown/eng/CTO_BRIEF_ACQUISITION_TRACKING.md §4.3.
        try {
          const firstTouch: FirstTouchPayload | null = readFirstTouchForSignup();
          const completeSignup = httpsCallable(functions, 'completeSignup');
          await completeSignup({ firstTouch });
        } catch (acqErr) {
          console.warn('completeSignup failed (non-fatal):', acqErr);
        }
      } else {
        await signInWithEmailAndPassword(auth, email, pw);
      }
      nav('/dashboard');
    } catch (err: any) {
      setNotice({ kind: 'error', message: err.message });
    } finally {
      setIsLoading(false);
    }
  };

  const onForgotPassword = () => {
    if (!email) {
      setNotice({ kind: 'error', message: 'Enter your email above and we will send a reset link.' });
      return;
    }
    sendPasswordResetEmail(auth, email)
      .then(() =>
        setNotice({
          kind: 'info',
          message: 'A reset link is on its way. Check your inbox in a moment.',
        })
      )
      .catch((err) => setNotice({ kind: 'error', message: err.message }));
  };

  const isSignup = mode === 'signup';

  return (
    <MarketingLayout activePage="home" minimalHeader>
      <Helmet>
        <title>{isSignup ? 'Begin your record · Melio' : 'Log in · Melio'}</title>
      </Helmet>

      <section className="mk-auth" aria-labelledby="auth-title">
        <div className="mk-container">
          <div className="mk-auth__card">
            <p className="t-details t-muted">{isSignup ? 'New member' : 'Members'}</p>
            <h1 id="auth-title" className="mk-auth__title t-display">
              {isSignup ? 'Begin your record.' : 'Welcome back.'}
            </h1>
            <p className="mk-auth__hint t-body">
              {isSignup
                ? 'A few details so we can begin a record in your name. Phone and postcode can wait.'
                : 'Sign in to read your record.'}
            </p>

            {notice && (
              <div
                className={`mk-notice t-body ${notice.kind === 'error' ? 'mk-notice--error' : ''}`.trim()}
                role={notice.kind === 'error' ? 'alert' : 'status'}
              >
                {notice.message}
              </div>
            )}

            <form onSubmit={onSubmit}>
              {isSignup && (
                <div className="mk-field-row">
                  <label className="mk-field" htmlFor="firstName">
                    <span className="mk-field__label t-details">First name</span>
                    <input
                      id="firstName"
                      className="mk-field__input"
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      autoComplete="given-name"
                      disabled={isLoading}
                    />
                  </label>
                  <label className="mk-field" htmlFor="lastName">
                    <span className="mk-field__label t-details">Last name</span>
                    <input
                      id="lastName"
                      className="mk-field__input"
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      autoComplete="family-name"
                      disabled={isLoading}
                    />
                  </label>
                </div>
              )}

              <label className="mk-field" htmlFor="email">
                <span className="mk-field__label t-details">Email</span>
                <input
                  id="email"
                  className="mk-field__input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  disabled={isLoading}
                />
              </label>

              {isSignup && (
                <div className="mk-field-row">
                  <label className="mk-field" htmlFor="phone">
                    <span className="mk-field__label t-details">
                      Phone <span className="mk-field__optional">· optional</span>
                    </span>
                    <input
                      id="phone"
                      className="mk-field__input"
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      autoComplete="tel"
                      disabled={isLoading}
                    />
                  </label>
                  {/* Medical Emergency §5.1(1) requires "a United Kingdom address
                      to which emergency services can be directed" — so a postcode
                      is not optional, whatever else on this form is. */}
                  <label className="mk-field" htmlFor="postcode">
                    <span className="mk-field__label t-details">Postcode</span>
                    <input
                      id="postcode"
                      className="mk-field__input"
                      type="text"
                      value={postcode}
                      onChange={(e) => setPostcode(e.target.value)}
                      autoComplete="postal-code"
                      disabled={isLoading}
                    />
                  </label>
                </div>
              )}

              <div className="mk-field">
                <div className="mk-field__label t-details">
                  <label htmlFor="password">Password</label>
                  {!isSignup && (
                    <button
                      type="button"
                      onClick={onForgotPassword}
                      disabled={isLoading}
                      className="mk-btn mk-btn--link"
                      style={{ fontSize: 'inherit', letterSpacing: 'inherit' }}
                    >
                      Forgot?
                    </button>
                  )}
                </div>
                <input
                  id="password"
                  className="mk-field__input"
                  type="password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  required
                  autoComplete={isSignup ? 'new-password' : 'current-password'}
                  disabled={isLoading}
                />
              </div>

              <button type="submit" className="mk-btn mk-btn--fill mk-auth__submit" disabled={isLoading}>
                {isLoading ? 'One moment…' : isSignup ? 'Begin' : 'Continue'}
              </button>
            </form>

            <p className="mk-auth__foot t-body">
              {isSignup ? (
                <>
                  Already a member? <Link to="/login">Log in</Link>
                </>
              ) : (
                <>
                  Not yet a member? <Link to="/waitlist">Join the waitlist</Link>
                </>
              )}
            </p>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
